import { BaseModel } from "./base.model";
import { FieldTypes } from "./field.types";
import { MetaDataStore } from "./datastore";
import {
  FormModel,
  Row as BaseRow,
  Expression as BaseExpression,
  Property as BaseProperty,
  Appearance as BaseAppearance,
  Criteria as BaseCriteria,
  QueryDefinition as BaseQueryDefinition
} from "./form.model";
import {
  FieldColumn as BaseFieldColumn,
  SectionColumn as BaseSectionColumn,
  RichTextColumn as BaseRichTextColumn,
  ModelColumn as BaseModelColumn
} from "./column.model";
import {
  AggregationField as BaseAggregationField,
  ReferenceField as BaseReferenceField
} from "./field.model";
import { registerModels as registerBaseModels } from "./index";

export function ProcessMetadataWrapper(jsonData, id = jsonData["Root"]) {
  registerModels();
  let kind = jsonData[id].Kind;
  let dynamicClass = ProcessModel.getMetadata(kind);
  let ds = new MetaDataStore(jsonData);
  let modelIns = new dynamicClass(ds, id, dynamicClass);
  ds.cache.set(id, modelIns);
  return modelIns;
}

function registerModels() {
  registerBaseModels();
  registerProcessModels();
}

export function registerProcessModels() {
  ProcessModel.registerModel(ProcessModel, "Model");
  ProcessModel.registerModel(ProcessRow, "Row");
  ProcessModel.registerModel(FieldColumn);
  ProcessModel.registerModel(SectionColumn);
  ProcessModel.registerModel(RichTextColumn);
  ProcessModel.registerModel(ModelColumn);
  ProcessModel.registerModel(AggregateField);
  ProcessModel.registerModel(ReferenceField);
  ProcessModel.registerModel(Expression);
  ProcessModel.registerModel(Property);
  ProcessModel.registerModel(Appearance);
  ProcessModel.registerModel(Criteria);
  ProcessModel.registerModel(QueryDefinition);
  ProcessModel.registerModel(ProcessDef);
  ProcessModel.registerModel(Activity);
  ProcessModel.registerModel(Permission);
  ProcessModel.registerModel(RefreshField);
  ProcessModel.registerModel(Resource);
  ProcessModel.registerModel(SLA);
  ProcessModel.registerModel(Notify);
  ProcessModel.registerModel(SendBackActivity);
}

export class ProcessRow extends BaseRow {}
ProcessRow.setFieldTypes({
  Insight: FieldTypes.kindOf("Model"),
  Button: FieldTypes.kindOf("Model")
});
ProcessRow.setOneToManyList(["Initiator::Column"]);
ProcessRow.setManyToOneList([
  "Button::Model", // will be used to create/delete button row
  "Insight::Model" //will be used to create/delete inshite in model
]);

class FieldColumn extends BaseFieldColumn {}

FieldColumn.setFieldTypes({
  Permission: FieldTypes.string
});

FieldColumn.setOneToManyList([
  "Column::Permission",
  "ActivityColumn::Appearance"
]);

class SectionColumn extends BaseSectionColumn {}

SectionColumn.setFieldTypes({
  Permission: FieldTypes.string
});

SectionColumn.setOneToManyList(["Column::Permission"]);

class RichTextColumn extends BaseRichTextColumn {}

RichTextColumn.setOneToManyList(["Column::Permission"]);

class ModelColumn extends BaseModelColumn {}

ModelColumn.setOneToManyList(["Column::Permission"]);

class AggregateField extends BaseAggregationField {}
AggregateField.setOneToManyList(["Field::RefreshField"]);

class ReferenceField extends BaseReferenceField {}
ReferenceField.setOneToManyList(["Field::RefreshField"]);

class Expression extends BaseExpression {}
Expression.setFieldTypes({
  Activity: FieldTypes.kindOf("Activity"),
  ProcessDef: FieldTypes.kindOf("ProcessDef"),
  SLA: FieldTypes.kindOf("SLA"),
  Notify: FieldTypes.kindOf("Notify"),
  Resource: FieldTypes.kindOf("Resource"),
  Property: FieldTypes.kindOf("Property")
});
Expression.setManyToOneList([
  "Activity::Activity", //will be used to create/delete Expression
  "ProcessDef::ProcessDef",
  "SLA::SLA",
  "Notify::Notify",
  "Resource::Resource",
  "Property::Property"
]);

class Property extends BaseProperty {}
Property.setOneToManyList(["Property::Expression"]);
Property.setManyToOneList(["Field::Field"]);

class Appearance extends BaseAppearance {}
Appearance.setManyToOneList([
  "ActivityColumn::Column", // to create / delete step wise appearance many to many
  "Activity::Activity" // to create / delete step wise appearance
]);
Appearance.setFieldTypes({
  ActivityColumn: FieldTypes.kindOf("Column"),
  Activity: FieldTypes.kindOf("Activity")
});

class Criteria extends BaseCriteria {}
Criteria.setManyToOneList([
  "Permission::Permission", // will be used create/delete button validation
  "SLA::SLA" // will be used to create/delete activity sla condition
]);
Criteria.setFieldTypes({
  Permission: FieldTypes.kindOf("Permission"),
  SLA: FieldTypes.kindOf("SLA")
});

class QueryDefinition extends BaseQueryDefinition {}
QueryDefinition.setManyToOneList(["Resource::Resource"]);
QueryDefinition.setFieldTypes({
  Resource: FieldTypes.kindOf("Resource") // dataset assignment in workflow
});

class ProcessDef extends BaseModel {
  isRoot() {
    return Boolean(this.getValue("Model"));
  }

  getParent() {
    return this.isRoot() ? this.get("Model") : this.get("Activity");
  }

  getActivities() {
    return this.getList("ProcessDef", "Activity");
  }

  deleteChild(childName, refName = "ProcessDef") {
    let propertyList = this.getList(refName, childName);
    propertyList.forEach((property) => {
      this.delete(childName, property.id, refName);
    });
  }

  suspend() {
    this.updateMulti({
      IsSuspended: true,
      SuspendedAt: new Date().toISOString(),
      // to mitigate the publish error while deleting the branch without name
      Name: this.getValue("Name") || "Unnamed Branch"
    });
    this.deleteChild("Expression");
    let activityList = this.getActivities();
    activityList.forEach((activity) => {
      activity.suspend();
    });
  }

  isSuspended() {
    return Boolean(this.getValue("IsSuspended")) && !window.showSuspendedStep;
  }

  getSuspendedAt() {
    return this.getValue("SuspendedAt");
  }
}

ProcessDef.setOneToManyList(["ProcessDef::Activity", "ProcessDef::Expression"]);

ProcessDef.setManyToOneList([
  "Model::Model", //will be used to create/delete ProcessDef
  "Activity::Activity" //will be used to create/delete ProcessDef
]);

ProcessDef.setFieldTypes({
  Name: FieldTypes.string,
  Model: FieldTypes.kindOf("Model"),
  Activity: FieldTypes.kindOf("Activity"),
  WorkflowType: FieldTypes.string,
  IsSuspended: FieldTypes.boolean,
  SuspendedAt: FieldTypes.string
});

class Activity extends BaseModel {
  getType() {
    return this.getValue("NodeType");
  }

  getParent() {
    return this.get("ProcessDef");
  }

  getProcessDefs() {
    return this.getList("Activity", "ProcessDef");
  }

  deleteChild(childName, refName = "Activity") {
    let propertyList = this.getList(refName, childName);
    propertyList.forEach((property) => {
      this.delete(childName, property.id, refName);
    });
  }

  suspend() {
    this.updateMulti({
      IsSuspended: true,
      SuspendedAt: new Date().toISOString(),
      Goto: null,
      // to mitigate the publish error while deleting the step without name
      Name: this.getValue("Name") || "Unnamed Step"
    });
    this.deleteChild("Resource");
    this.deleteChild("Resource", "SLABreach");
    this.deleteChild("SLA");
    this.deleteChild("SLA", "ActivityDefaultSLA");
    this.deleteChild("Expression");
    this.deleteChild("SendBackActivity");
    let processDefList = this.getProcessDefs();
    processDefList.forEach((processDef) => {
      processDef.suspend();
    });
    //clear permission's action complete validation & refresh field to fix
    // the field reference issue when activity deletion
    let permissions = this.getPermissions();
    permissions.forEach((permission) => {
      permission.deleteChild("Criteria");
      permission.deleteChild("RefreshField");
    });
  }

  isSuspended() {
    return (
      Boolean(this.getValue("IsSuspended") || this.getParent().isSuspended()) &&
      !window.showSuspendedStep
    );
  }

  getSuspendedAt() {
    return this.getValue("SuspendedAt");
  }

  getGotoActivity() {
    return this.get(this.getValue("Goto"));
  }

  getDependency() {
    return this.getList("Goto", "Activity").filter((gotoActivity) => {
      return !gotoActivity.isSuspended();
    });
  }

  getPermissions() {
    return this.getList("Activity", "Permission");
  }
}

Activity.setOneToManyList([
  "Activity::ProcessDef",
  "Activity::Resource",
  "SLABreach::Resource",
  "Activity::Permission",
  "Activity::Appearance",
  "Activity::SLA",
  "ActivityDefaultSLA::SLA", //default SLA for activity
  "Activity::Expression",
  "Activity::SendBackActivity"
]);

Activity.setManyToOneList([
  "ProcessDef::ProcessDef", //will be used to create/delete activity
  "Goto::Activity" // will be used for goto dependency
]);

Activity.setFieldTypes({
  ProcessDef: FieldTypes.kindOf("ProcessDef").isRequired,
  NodeType: FieldTypes.string.isRequired,
  Name: FieldTypes.string.isRequired,
  Description: FieldTypes.string,
  Goto: FieldTypes.kindOf("Activity"),
  Permission: FieldTypes.kindOf("Permission"),
  AssignmentRule: FieldTypes.string, // Any / RoundRobin / LeastLoaded
  ApproverCount: FieldTypes.number,
  ApproverUnit: FieldTypes.string,
  IsSuspended: FieldTypes.boolean,
  IsEmailActionEnabled: FieldTypes.boolean,
  SuspendedAt: FieldTypes.string,
  EnforcePreviousApprover: FieldTypes.boolean,
  IsSelfPickEnabled: FieldTypes.boolean
});

class Permission extends BaseModel {
  getCriteria() {
    return this.getList("Permission", "Criteria");
  }

  getRefreshField() {
    return this.getList("Permission", "RefreshField");
  }

  deleteChild(childName, refName = "Permission") {
    let propertyList = this.getList(refName, childName);
    propertyList.forEach((property) => {
      this.delete(childName, property.id, refName);
    });
  }
}
Permission.setOneToManyList([
  "Permission::Criteria",
  "Permission::RefreshField",
  "Permission::SendBackActivity"
]);
Permission.setManyToOneList([
  "Activity::Activity", //will be used to create/delete permission
  "Column::Column" //will be updated while creating permission via activity
]);
Permission.setFieldTypes({
  Activity: FieldTypes.kindOf("Activity").isRequired,
  Column: FieldTypes.kindOf("Column").isRequired,
  Permission: FieldTypes.string,
  Label: FieldTypes.string, // used to save the button name
  IsSendbackEnabled: FieldTypes.boolean
});

class SendBackActivity extends BaseModel {}
SendBackActivity.setManyToOneList([
  "Permission::Permission",
  "Activity::Activity"
]);
SendBackActivity.setFieldTypes({
  Name: FieldTypes.string,
  Permission: FieldTypes.kindOf("Permission"),
  Activity: FieldTypes.kindOf("Field")
});

class RefreshField extends BaseModel {}
RefreshField.setManyToOneList(["Field::Field", "Permission::Permission"]);
RefreshField.setFieldTypes({
  Name: FieldTypes.string,
  Permission: FieldTypes.kindOf("Permission"),
  Field: FieldTypes.kindOf("Field"),
  Model: FieldTypes.kindOf("Model"),
  RootModel: FieldTypes.kindOf("Model")
});

class Resource extends BaseModel {}
Resource.setOneToManyList([
  "Resource::QueryDefinition",
  "Resource::Expression"
]);
Resource.setManyToOneList([
  "Activity::Activity", // will be used for create/delete activity assignedTo,
  "SLABreach::SLA", // will be used for create/delete activity sla breach,
  "Field::Field" // will be used for field dependency check
]);
Resource.setFieldTypes({
  Activity: FieldTypes.kindOf("Activity"),
  SLABreach: FieldTypes.kindOf("Activity"),
  Field: FieldTypes.kindOf("Field"), // for field dependency
  Value: FieldTypes.string,
  ValueType: FieldTypes.string.isRequired, // User / Group / Field / Expression / Dataset
  DisplayValue: FieldTypes.string
});

class SLA extends BaseModel {}
SLA.setOneToManyList(["SLA::Notify", "SLA::Expression", "SLA::Criteria"]);
SLA.setManyToOneList(["Activity::Activity", "ActivityDefaultSLA::Activity"]);
SLA.setFieldTypes({
  Activity: FieldTypes.kindOf("Activity"),
  ActivityDefaultSLA: FieldTypes.kindOf("Activity"),
  Value: FieldTypes.number,
  Unit: FieldTypes.string
});

class Notify extends BaseModel {}
Notify.setOneToManyList(["Notify::Expression"]);
Notify.setManyToOneList(["SLA::SLA", "Field::Field"]);
Notify.setFieldTypes({
  SLA: FieldTypes.kindOf("SLA").isRequired,
  Type: FieldTypes.string, // Notify / Escalate
  When: FieldTypes.string, // before / after
  Unit: FieldTypes.string, // Minutes / Hrs / Days
  Value: FieldTypes.string,
  ValueType: FieldTypes.string, // User or Group or Field or Expression
  To: FieldTypes.string, // Will be user / group id
  Field: FieldTypes.kindOf("Field") // User or Email Field id
});

export class ProcessModel extends FormModel {}

ProcessModel.setOneToManyList([
  "Insight::Row",
  "Button::Row",
  "Model::ProcessDef"
]);

ProcessModel.setFieldTypes({
  RootProcessDef: FieldTypes.kindOf("ProcessDef")
});

registerProcessModels();
